import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { IDynamicKeyValue } from '@modeso/types__aumico-lib-core-be';
import {
    ComponentType,
    GetNotesTypeTranslationsDto,
    IEquityOptions,
    IFinancialLineItemOptions,
    IFreeTableOptions,
    IInputOptions,
    IMovementScheduleEditableColumnHeaders,
    IRichText,
    ISelectOptions,
    ISuperTableOptions,
    ISuperTableSegment,
    IXAxisConfigColumn,
    Options,
    SuperTableColumnType,
} from '@modeso/types__aumico-lib-notes-be';
import { UntilDestroy } from '@ngneat/until-destroy';
import { cloneDeep } from 'lodash-es';
import { EMPTY_STRING } from '../../../../../aumico-lib-core-fe/src/lib';
import { ITranslationsTableData } from '../../interfaces/translations-table-data.interface';

const SINGLE_ELEMENT_COMPONENT_OPTIONS = 0;
@UntilDestroy()
@Component({
    selector: 'aumico-notes-edit-notes-type-translations',
    templateUrl: './edit-notes-type-translations.component.html',
    styleUrls: ['./edit-notes-type-translations.component.scss'],
})
export class EditNotesTypeTranslationsComponent implements OnInit {
    @Input() notesType: GetNotesTypeTranslationsDto | undefined;
    @Input() currentLocale = 'de-ch';
    @Input() selectedLocale: string | undefined;
    @Input() panelOpenState = false;

    public componentNames = ComponentType;
    public notesTypeInformation: IRichText[] = [];
    public notesTypeTitle: IDynamicKeyValue | undefined;

    public equityOptions: IEquityOptions | undefined;
    public richTextOptions: IRichText[] | undefined;
    public financialLineItemOptions: IFinancialLineItemOptions | undefined;
    public movementScheduleOptions: IMovementScheduleEditableColumnHeaders | undefined;
    public selectOptions: ISelectOptions | undefined;
    public inputOptions: IInputOptions | undefined;
    public freeTableOptions: IFreeTableOptions | undefined;
    public superTableOptions: ISuperTableOptions | undefined;

    private notesTypeInformationToBeSaved: IDynamicKeyValue | undefined;
    private notesTypeTitleToBeSaved: IDynamicKeyValue | undefined;
    private notesTypeTranslationsToBeSaved: GetNotesTypeTranslationsDto | undefined;

    @Output() translationValuesChanged: EventEmitter<GetNotesTypeTranslationsDto> =
        new EventEmitter<GetNotesTypeTranslationsDto>();
    richTextTitles: IRichText[] = [];

    ngOnInit(): void {
        this.setNotesTypeOptions();
        this.mapNotesInformationToRichTextInput();
        this.notesTypeTitle = this.notesType.title;
    }

    private setNotesTypeOptions(): void {
        this.notesTypeTranslationsToBeSaved = cloneDeep(this.notesType);
        this.notesTypeInformationToBeSaved = cloneDeep(this.notesType.information);
        this.notesType.componentsOptions.forEach((option) => {
            switch (option.componentName) {
                case ComponentType.EQUITY:
                    this.equityOptions = option.componentOptions[SINGLE_ELEMENT_COMPONENT_OPTIONS] as IEquityOptions;
                    break;
                case ComponentType.SELECT:
                    this.selectOptions = option.componentOptions[SINGLE_ELEMENT_COMPONENT_OPTIONS] as ISelectOptions;
                    break;
                case ComponentType.INPUT:
                    this.inputOptions = option.componentOptions[SINGLE_ELEMENT_COMPONENT_OPTIONS] as IInputOptions;
                    break;
                case ComponentType.RICH_TEXT:
                    this.richTextOptions = option.componentOptions as IRichText[];
                    this.richTextTitles = this.richTextOptions.map((richText) => ({
                        text: richText.title,
                        language: richText.language,
                    }));
                    break;
                case ComponentType.MOVEMENT_SCHEDULE:
                    this.movementScheduleOptions = option.componentOptions[
                        SINGLE_ELEMENT_COMPONENT_OPTIONS
                    ] as IMovementScheduleEditableColumnHeaders;
                    break;
                case ComponentType.FINANCIAL_LINE_ITEM:
                    this.financialLineItemOptions = option.componentOptions[
                        SINGLE_ELEMENT_COMPONENT_OPTIONS
                    ] as IFinancialLineItemOptions;
                    break;
                case ComponentType.FREE_TABLE_WITH_AMOUNTS:
                    this.freeTableOptions = option.componentOptions[
                        SINGLE_ELEMENT_COMPONENT_OPTIONS
                    ] as IFreeTableOptions;
                    break;
                case ComponentType.SUPER_TABLE:
                    this.superTableOptions = option.componentOptions[
                        SINGLE_ELEMENT_COMPONENT_OPTIONS
                    ] as ISuperTableOptions;
                    break;

                default:
                    throw new Error('This component type does not exist.');
            }
        });
    }

    private mapNotesInformationToRichTextInput(): void {
        Object.keys(this.notesType.information).forEach((key) => {
            this.notesTypeInformation.push({
                language: key,
                text: this.notesType.information[key],
            });
        });
    }

    public onInformationValuesChanged(formValue: ITranslationsTableData): void {
        this.notesTypeInformationToBeSaved = {
            [this.currentLocale]: formValue.currentLocale,
            [this.selectedLocale]: formValue.selectedLocale,
        };
        this.getNotesTypeTranslationsToBeSaved();
    }

    public onTitleValueChanged(formValue: ITranslationsTableData): void {
        this.notesTypeTitleToBeSaved = {
            [this.currentLocale]: formValue.currentLocale,
            [this.selectedLocale]: formValue.selectedLocale,
        };
        this.getNotesTypeTranslationsToBeSaved();
    }

    public onEquityComponentValuesChanged(formValue: ITranslationsTableData[]): void {
        const textTitle: IDynamicKeyValue = {
            [this.currentLocale]: formValue.find((value) => value.key === 'textTitle').currentLocale,
            [this.selectedLocale]: formValue.find((value) => value.key === 'textTitle').selectedLocale,
        };
        const currentValueTitle1: IDynamicKeyValue = {
            [this.currentLocale]: formValue.find((value) => value.key === 'currentValueTitle1').currentLocale,
            [this.selectedLocale]: formValue.find((value) => value.key === 'currentValueTitle1').selectedLocale,
        };
        const currentValueTitle2: IDynamicKeyValue = {
            [this.currentLocale]: formValue.find((value) => value.key === 'currentValueTitle2').currentLocale,
            [this.selectedLocale]: formValue.find((value) => value.key === 'currentValueTitle2').selectedLocale,
        };
        const previousValueTitle1: IDynamicKeyValue = {
            [this.currentLocale]: formValue.find((value) => value.key === 'previousValueTitle1').currentLocale,
            [this.selectedLocale]: formValue.find((value) => value.key === 'previousValueTitle1').selectedLocale,
        };
        const previousValueTitle2: IDynamicKeyValue = {
            [this.currentLocale]: formValue.find((value) => value.key === 'previousValueTitle2').currentLocale,
            [this.selectedLocale]: formValue.find((value) => value.key === 'previousValueTitle2').selectedLocale,
        };
        this.equityOptions = {
            ...this.equityOptions,
            textTitle: textTitle,
            currentValueTitle1: currentValueTitle1,
            currentValueTitle2: currentValueTitle2,
            previousValueTitle1: previousValueTitle1,
            previousValueTitle2: previousValueTitle2,
        };
        this.getNotesTypeTranslationsToBeSaved();
    }

    public onRichTextComponentValuesChanged(formValue: ITranslationsTableData): void {
        this.richTextOptions = [
            {
                text: formValue.currentLocale,
                language: this.currentLocale,
            },
            {
                text: formValue.selectedLocale,
                language: this.selectedLocale,
            },
        ];
        this.getNotesTypeTranslationsToBeSaved();
    }

    public onRichTextTitleValuesChanged(formValue: ITranslationsTableData): void {
        this.richTextTitles = [
            {
                title: formValue.currentLocale,
                language: this.currentLocale,
            },
            {
                title: formValue.selectedLocale,
                language: this.selectedLocale,
            },
        ] as IRichText[];
        this.getNotesTypeTranslationsToBeSaved();
    }

    public onMovementScheduleComponentValuesChanged(formValue: ITranslationsTableData[]): void {
        const column1: IDynamicKeyValue = {
            [this.currentLocale]: formValue.find((value) => value.key === 'column1').currentLocale,
            [this.selectedLocale]: formValue.find((value) => value.key === 'column1').selectedLocale,
        };
        const column2: IDynamicKeyValue = {
            [this.currentLocale]: formValue.find((value) => value.key === 'column2').currentLocale,
            [this.selectedLocale]: formValue.find((value) => value.key === 'column2').selectedLocale,
        };
        const column3: IDynamicKeyValue = {
            [this.currentLocale]: formValue.find((value) => value.key === 'column3').currentLocale,
            [this.selectedLocale]: formValue.find((value) => value.key === 'column3').selectedLocale,
        };
        const column4: IDynamicKeyValue = {
            [this.currentLocale]: formValue.find((value) => value.key === 'column4').currentLocale,
            [this.selectedLocale]: formValue.find((value) => value.key === 'column4').selectedLocale,
        };
        const formTitle = formValue.find((value) => value.key === 'title');
        const title: IDynamicKeyValue = {
            [this.currentLocale]: formTitle ? formTitle.currentLocale : EMPTY_STRING,
            [this.selectedLocale]: formTitle ? formTitle.selectedLocale : EMPTY_STRING,
        };
        this.movementScheduleOptions = {
            column1: column1,
            column2: column2,
            column3: column3,
            column4: column4,
            title,
        };
        this.getNotesTypeTranslationsToBeSaved();
    }

    public onFinancialLineItemComponentValuesChanged(formValue: ITranslationsTableData[]): void {
        this.financialLineItemOptions = {
            text: {
                [this.currentLocale]: formValue.find((value) => value.key === 'text').currentLocale,
                [this.selectedLocale]: formValue.find((value) => value.key === 'text').selectedLocale,
            },
        };
        this.getNotesTypeTranslationsToBeSaved();
    }

    public onFreeTableComponentValuesChanged(formValue: ITranslationsTableData[]): void {
        const formTitle = formValue.find((value) => value.key === 'title');
        this.freeTableOptions = {
            ...this.freeTableOptions,
            title: {
                [this.currentLocale]: formTitle ? formTitle.currentLocale : EMPTY_STRING,
                [this.selectedLocale]: formTitle ? formTitle.selectedLocale : EMPTY_STRING,
            },
        };
        this.getNotesTypeTranslationsToBeSaved();
    }

    public onSelectComponentValuesChanged(formValue: ITranslationsTableData[]): void {
        const text: IDynamicKeyValue = {
            [this.currentLocale]: formValue.find((value) => value.key === 'text').currentLocale,
            [this.selectedLocale]: formValue.find((value) => value.key === 'text').selectedLocale,
        };
        const formTitle = formValue.find((value) => value.key === 'title');
        const title: Record<string, string> = {
            [this.currentLocale]: formTitle ? formTitle.currentLocale : EMPTY_STRING,
            [this.selectedLocale]: formTitle ? formTitle.selectedLocale : EMPTY_STRING,
        };
        const selectionOptions: IDynamicKeyValue[] = formValue
            .filter((value) => value.key === 'selectionOption')
            .map((selectionOption) => {
                return {
                    [this.currentLocale]: selectionOption.currentLocale,
                    [this.selectedLocale]: selectionOption.selectedLocale,
                };
            });
        this.selectOptions = {
            text: text,
            selectionOption: selectionOptions,
            title,
        };
        this.getNotesTypeTranslationsToBeSaved();
    }

    public onInputComponentValuesChanged(formValue: ITranslationsTableData[]): void {
        const text: IDynamicKeyValue = {
            [this.currentLocale]: formValue.find((value) => value.key === 'text').currentLocale,
            [this.selectedLocale]: formValue.find((value) => value.key === 'text').selectedLocale,
        };
        const formTitle = formValue.find((value) => value.key === 'title');
        const title: Record<string, string> = {
            [this.currentLocale]: formTitle ? formTitle.currentLocale : EMPTY_STRING,
            [this.selectedLocale]: formTitle ? formTitle.selectedLocale : EMPTY_STRING,
        };
        this.inputOptions = {
            ...this.inputOptions,
            text,
            title,
        };
        this.getNotesTypeTranslationsToBeSaved();
    }

    public onSuperTableComponentValuesChanged(formValue: ITranslationsTableData[]): void {
        const title: IDynamicKeyValue = {
            [this.currentLocale]: formValue.find((value) => value.key === 'title').currentLocale,
            [this.selectedLocale]: formValue.find((value) => value.key === 'title').selectedLocale,
        };

        const xAxisConfig = cloneDeep<IXAxisConfigColumn[]>(this.superTableOptions.xAxisConfig);
        let xAxisConfigColIndex = 1;
        xAxisConfig.forEach((xAxisConfigCol) => {
            if (
                xAxisConfigCol.type === SuperTableColumnType.NUMBER ||
                xAxisConfigCol.type === SuperTableColumnType.TEXT
            ) {
                const freeValueTitle = formValue.find((value) => value.key === 'freeValueTitle' + xAxisConfigColIndex);

                xAxisConfigCol.title = {
                    [this.currentLocale]: freeValueTitle.currentLocale,
                    [this.selectedLocale]: freeValueTitle.selectedLocale,
                };
                xAxisConfigColIndex = xAxisConfigColIndex + 1;
            }
        });

        const segments: ISuperTableSegment[] = cloneDeep<ISuperTableSegment[]>(this.superTableOptions.segments);
        let segmentIndex = 1;
        segments.forEach((segment) => {
            if (segment.title != null) {
                const segmentTitle = formValue.find((value) => value.key === 'segmentTitle' + segmentIndex);
                segment.title = {
                    [this.currentLocale]: segmentTitle.currentLocale,
                    [this.selectedLocale]: segmentTitle.selectedLocale,
                };
                segmentIndex = segmentIndex + 1;
            }
        });

        this.superTableOptions = {
            ...this.superTableOptions,
            title: title,
            xAxisConfig,
            segments,
        };
        this.getNotesTypeTranslationsToBeSaved();
    }

    private getNotesTypeTranslationsToBeSaved(): void {
        this.notesTypeTranslationsToBeSaved.componentsOptions.forEach((componentOption) => {
            switch (componentOption.componentName) {
                case ComponentType.EQUITY:
                    componentOption.componentOptions[SINGLE_ELEMENT_COMPONENT_OPTIONS] = this.equityOptions;
                    break;
                case ComponentType.FINANCIAL_LINE_ITEM:
                    componentOption.componentOptions[SINGLE_ELEMENT_COMPONENT_OPTIONS] = this.financialLineItemOptions;
                    break;
                case ComponentType.MOVEMENT_SCHEDULE:
                    componentOption.componentOptions[SINGLE_ELEMENT_COMPONENT_OPTIONS] = this.movementScheduleOptions;
                    break;
                case ComponentType.SELECT:
                    componentOption.componentOptions[SINGLE_ELEMENT_COMPONENT_OPTIONS] = this.selectOptions;
                    break;
                case ComponentType.INPUT:
                    componentOption.componentOptions[SINGLE_ELEMENT_COMPONENT_OPTIONS] = this.inputOptions;
                    break;
                case ComponentType.FREE_TABLE_WITH_AMOUNTS:
                    componentOption.componentOptions[SINGLE_ELEMENT_COMPONENT_OPTIONS] = this.freeTableOptions;
                    break;
                case ComponentType.RICH_TEXT:
                    componentOption.componentOptions = this.mapRichTextTitlesAndOptions();
                    break;
                case ComponentType.SUPER_TABLE:
                    componentOption.componentOptions[SINGLE_ELEMENT_COMPONENT_OPTIONS] = this.superTableOptions;
            }
        });
        this.notesTypeTranslationsToBeSaved.information = this.notesTypeInformationToBeSaved;
        this.notesTypeTranslationsToBeSaved.title = this.notesTypeTitleToBeSaved;
        if (
            this.selectedLocale != null &&
            this.notesTypeTranslationsToBeSaved.title != null &&
            this.notesTypeTranslationsToBeSaved.information != null
        ) {
            this.translationValuesChanged.emit(this.notesTypeTranslationsToBeSaved);
        }
    }
    private mapRichTextTitlesAndOptions(): Options[] {
        return this.richTextOptions.map((richText) => {
            const richTextInTitle = this.richTextTitles.find((title) => title.language === richText.language);
            return {
                language: richText.language,
                text: richText.text,
                title: richTextInTitle?.title,
            };
        });
    }
}
